/**
 * Auth Context for RC Studios
 * Provides Supabase authentication state and methods throughout the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { authAPI, isImpersonating, clearImpersonationToken, authFetch } from '@/lib/auth';

const PROFILE_FIELDS = ['full_name', 'company', 'phone', 'referral_source', 'referral_other'];

// Save the signup answers once the new user is signed in (keeps redirect_after for EmailConfirmed).
const applyPendingProfile = async (userData) => {
  let pending;
  try { pending = JSON.parse(localStorage.getItem('rc_pending_profile')); } catch { return userData; }
  if (!pending || pending.applied || isImpersonating()) return userData;
  const body = Object.fromEntries(PROFILE_FIELDS.filter((k) => pending[k] && !userData?.[k]).map((k) => [k, pending[k]]));
  localStorage.setItem('rc_pending_profile', JSON.stringify({ redirect_after: pending.redirect_after, applied: true }));
  if (!Object.keys(body).length) return userData;
  const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/profile`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return res.ok ? authAPI.getMe() : userData;
};

const AuthContext = createContext(null);

/**
 * Parse URL hash for Supabase auth tokens
 * Supabase recovery links look like: https://site/#access_token=...&type=recovery
 */
function parseHashParams(hash) {
  if (!hash || hash.length < 2) return {};
  const params = new URLSearchParams(hash.substring(1));
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [supabaseUser, setSupabaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use navigate for redirects (will be null on first render before Router is ready)
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user profile from backend (creates MongoDB profile if needed)
  const fetchUserProfile = useCallback(async () => {
    try {
      const userData = await applyPendingProfile(await authAPI.getMe());
      setUser(userData);
      setError(null);
      return userData;
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      setUser(null);
      return null;
    }
  }, []);

  // Check for recovery token in URL hash and redirect to /reset-password
  useEffect(() => {
    const checkRecoveryInHash = () => {
      const hash = window.location.hash;
      if (!hash) return false;
      
      const hashParams = parseHashParams(hash);
      
      // Check if this is a recovery link (type=recovery in hash)
      if (hashParams.type === 'recovery' && hashParams.access_token) {
        console.log('[Auth] Recovery token detected in URL hash, redirecting to /reset-password');
        
        // If we're not already on /reset-password, redirect there
        // Keep the hash so Supabase can process it
        if (location.pathname !== '/reset-password') {
          navigate('/reset-password' + hash, { replace: true });
          return true;
        }
      }
      return false;
    };

    checkRecoveryInHash();
  }, [location.pathname, navigate]);

  // Listen for Supabase auth state changes
  useEffect(() => {
    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSupabaseUser(session?.user || null);
        
        if (session?.user) {
          await fetchUserProfile();
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth init error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[Auth] State change:', event, 'path:', window.location.pathname);
      setSupabaseUser(session?.user || null);
      
      if (event === 'SIGNED_IN' && session?.user) {
        setLoading(true);
        await fetchUserProfile();
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        clearImpersonationToken();
      } else if (event === 'PASSWORD_RECOVERY') {
        // User clicked password reset link - Supabase has processed the token
        // Redirect to /reset-password if not already there
        console.log('[Auth] PASSWORD_RECOVERY event received');
        if (window.location.pathname !== '/reset-password') {
          console.log('[Auth] Redirecting to /reset-password');
          navigate('/reset-password', { replace: true });
        }
      } else if (event === 'USER_UPDATED') {
        // User updated (e.g., password changed)
        await fetchUserProfile();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchUserProfile, navigate]);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await authAPI.login(email, password);
      const profile = await fetchUserProfile();
      return { success: true, user: profile };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (email, password, returnPath = '/') => {
    setError(null);
    try {
      const result = await authAPI.register(email, password, returnPath);
      return { success: true, message: result.message };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Logout function
  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    setSupabaseUser(null);
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return null;
    }
  };

  // Check auth status
  const checkAuth = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    await fetchUserProfile();
    setLoading(false);
  }, [fetchUserProfile]);

  const value = {
    user,
    supabaseUser,
    loading,
    error,
    isAuthenticated: !!user || !!supabaseUser,
    isAdmin: user?.roles?.includes('admin') || false,
    login,
    register,
    logout,
    refreshUser,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
